import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Gift, Sparkles, Package, Download, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal, Overline } from "@/components/Reveal";

export default function Account() {
    const { user, refresh } = useAuth();
    const [loyalty, setLoyalty] = useState(null);
    const [orders, setOrders] = useState({ orders: [], bookings: [] });
    const [downloads, setDownloads] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/loyalty/me").then((r) => setLoyalty(r.data));
        api.get("/orders/me").then((r) => setOrders(r.data));
        api.get("/downloads/me").then((r) => setDownloads(r.data));
    };
    useEffect(() => { load(); }, []);

    const refLink = `${window.location.origin}/register?ref=${loyalty?.referral_code || ""}`;

    const cancel = async () => {
        if (!window.confirm("Cancel your membership at the end of the billing period?")) return;
        setBusy(true);
        try { await api.post("/membership/cancel"); toast.success("Membership will end at period close."); await refresh(); load(); }
        catch { toast.error("Could not cancel."); }
        setBusy(false);
    };
    const reactivate = async () => {
        setBusy(true);
        try { await api.post("/membership/reactivate"); toast.success("Membership resumed."); await refresh(); load(); }
        catch { toast.error("Could not resume."); }
        setBusy(false);
    };

    const grab = async (d) => {
        try {
            const res = await api.get(`/downloads/${d.order_id}/${d.product_id}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = d.filename || "download"; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Download failed."); }
    };

    const status = loyalty?.membership_status;

    return (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
            <Reveal>
                <Overline>Your Path</Overline>
                <h1 className="mt-3 font-serif text-5xl text-cream">Hello, {user?.name}</h1>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-3 gap-6">
                <div className="p-7 rounded-2xl bg-surface2 border border-gold/25 glow-gold" data-testid="loyalty-points">
                    <Sparkles className="w-6 h-6 text-gold mb-3" />
                    <div className="text-4xl font-serif text-cream">{loyalty?.points ?? 0}</div>
                    <div className="text-sm text-ash mt-1">Loyalty points · worth ${((loyalty?.points ?? 0) / 20).toFixed(2)}</div>
                </div>

                <div className="p-7 rounded-2xl bg-surface border border-gold/15" data-testid="membership-card">
                    <Gift className="w-6 h-6 text-gold mb-3" />
                    <div className="text-lg text-cream">Membership</div>
                    {status === "active" && <>
                        <div className="text-sm text-gold mt-1">{loyalty.membership_tier} · Active</div>
                        <div className="text-xs text-ash mt-1">Enjoy {user?.discount_percent}% off all supplies.</div>
                        <div className="mt-3 flex gap-2">
                            <Link to="/membership" className="text-xs text-gold border border-gold/30 rounded-full px-3 py-1.5 hover:bg-gold/10">Change plan</Link>
                            <button disabled={busy} onClick={cancel} data-testid="cancel-membership" className="text-xs text-ash border border-gold/20 rounded-full px-3 py-1.5 hover:text-clay inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancel</button>
                        </div>
                    </>}
                    {status === "cancelling" && <>
                        <div className="text-sm text-ash mt-1">{loyalty.membership_tier} · Ending soon</div>
                        <div className="text-xs text-ash mt-1">Access continues until the period closes.</div>
                        <button disabled={busy} onClick={reactivate} data-testid="reactivate-membership" className="mt-3 text-xs text-gold border border-gold/30 rounded-full px-3 py-1.5 hover:bg-gold/10 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Resume membership</button>
                    </>}
                    {(!status || status === "none") && <>
                        <div className="text-sm text-ash mt-1">No active plan</div>
                        <Link to="/membership" className="mt-3 inline-block text-xs text-gold border border-gold/30 rounded-full px-3 py-1.5 hover:bg-gold/10">Become a member</Link>
                    </>}
                </div>

                <div className="p-7 rounded-2xl bg-surface border border-gold/15">
                    <div className="overline-label">Refer a friend</div>
                    <div className="mt-2 text-sm text-ash">Earn 100 points per signup.</div>
                    <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); }}
                        data-testid="copy-referral" className="mt-3 inline-flex items-center gap-2 text-gold text-sm border border-gold/30 rounded-full px-4 py-2 hover:bg-gold/10 transition-colors">
                        <Copy className="w-4 h-4" /> Copy referral link
                    </button>
                </div>
            </div>

            {downloads.length > 0 && (
                <div className="mt-12">
                    <h2 className="font-serif text-3xl text-cream mb-5 flex items-center gap-3"><Download className="w-6 h-6 text-gold" /> My Downloads</h2>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="account-downloads">
                        {downloads.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-surface border border-gold/10">
                                <span className="text-cream text-sm">{d.name}</span>
                                <button onClick={() => grab(d)} data-testid={`download-${d.product_id}`} className="inline-flex items-center gap-2 text-gold text-sm border border-gold/30 rounded-full px-4 py-1.5 hover:bg-gold/10"><Download className="w-4 h-4" /> Download</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-12 grid md:grid-cols-2 gap-10">
                <div>
                    <h2 className="font-serif text-3xl text-cream mb-5 flex items-center gap-3"><Package className="w-6 h-6 text-gold" /> Orders &amp; Bookings</h2>
                    <div className="space-y-3" data-testid="account-orders">
                        {[...orders.orders.map((o) => ({ ...o, kind: "Order", label: `${o.items?.length || 0} item(s)`, amt: o.total })),
                          ...orders.bookings.map((b) => ({ ...b, kind: "Booking", label: b.service_name, amt: b.price }))]
                            .sort((a, b) => (b.created_at > a.created_at ? 1 : -1))
                            .map((o) => (
                                <div key={o.id} className="flex items-center justify-between p-4 rounded-xl bg-surface border border-gold/10">
                                    <div>
                                        <div className="text-cream text-sm">{o.kind} · {o.label}</div>
                                        <div className="text-xs text-faded">{new Date(o.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-gold">${o.amt.toFixed(2)}</div>
                                        <span className={`text-[10px] uppercase tracking-widest ${o.status === "paid" ? "text-gold" : "text-faded"}`}>{o.status}</span>
                                    </div>
                                </div>
                            ))}
                        {orders.orders.length === 0 && orders.bookings.length === 0 && <p className="text-ash text-sm">No orders yet.</p>}
                    </div>
                </div>

                <div>
                    <h2 className="font-serif text-3xl text-cream mb-5">Points History</h2>
                    <div className="space-y-2" data-testid="loyalty-history">
                        {(loyalty?.transactions || []).map((t) => (
                            <div key={t.id} className="flex items-center justify-between p-3 rounded-xl bg-surface border border-gold/10 text-sm">
                                <span className="text-ash">{t.reason}</span>
                                <span className={t.type === "earn" ? "text-gold" : "text-clay"}>{t.type === "earn" ? "+" : "-"}{t.points}</span>
                            </div>
                        ))}
                        {(!loyalty?.transactions || loyalty.transactions.length === 0) && <p className="text-ash text-sm">Earn points on your first purchase.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}
